<script setup>
import { ref, onMounted, computed, nextTick, onUnmounted, watch } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { isIphone } from "@/utils/functions";
import { useModals } from "@/composables/useModals";
import { smartPixel , trackAdriver} from "@/utils/metrika";
import BaseButtonUploadReceipt from "../ui/BaseButtonUploadReceipt.vue";

const { openModal, modalInfo } = useModals();
const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();

const activeBtn = ref("grand");
const grandPrize = ref([]);
const email = ref("");
const searchError = ref(false);
const isSearch = ref(false);
const lastSearchedEmail = ref("");
const scrollContainer = ref(null);
const sentinel = ref(null);
let observer = null;

const overflowAuto = computed(() => (storeRequests.winnersList?.length >= 2 ? "--overflow-auto" : ""));
const customScrollBar = computed(() => (storeRequests.winnersList?.length >= 2 && isIphone ? "list-scrollbar" : ""));
const isShowListWinners = computed(() => storeRequests.winnersList?.some((item) => item.winners.length > 0));

const counterDigits = computed(() => {
	const count = storeSettings.counterTicketsGrand || 0;
	const countStr = count.toString().padStart(7, "0");
	return countStr.split("");
});

// const winnersList = Array.from({ length: 0 }, (_, index) => {
// 	return {
// 		id: index + 1,
// 		winner: {
// 			name: "Александр Ф.",
// 			email: "le******@gmail.com",
// 		},
// 		prize: {
// 			id: 8,
// 			title: "Сертификат Яндекс Афиша",
// 			typ: "weekly_certificate",
// 			format: "certificate",
// 			image: "https://api.mistral2.testpromo.ru/media/IMGIMG7.png",
// 			description: "",
// 			greeting_text: "",
// 		},
// 		won_weekly_prize: false,
// 		won_fillword_prize: false,
// 		won_text_quiz_prize: false,
// 	};
// });

const activeFilterBtn = async (type) => {
	if (!!email.value) {
		await storeRequests.getEmailWinnings(email.value, type);
		activeBtn.value = type;
	} else {
		activeBtn.value = type;
		storeRequests.updateWinnings();
		await storeRequests.getWinnings(type);
		setupObserver();
	}
};

const isEmailChanged = computed(() => isSearch.value && email.value !== lastSearchedEmail.value);

const checkEmail = async () => {
	const emailPattern = /^(?![._%+\-])([A-Za-z0-9._%+\-]{1,})@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
	const isValid = emailPattern.test(email.value) && email.value.length >= 5;
	if (!!email.value && isValid) {
		await storeRequests.getEmailWinnings(email.value);
		isSearch.value = true;
		lastSearchedEmail.value = email.value;
		searchError.value = false;
	} else {
		searchError.value = true;
		isSearch.value = false;
	}
};

const clearEmail = async () => {
	email.value = "";
	searchError.value = false;
	lastSearchedEmail.value = "";
	isSearch.value = false;

	storeRequests.updateWinnings();
	await storeRequests.getWinnings();
	await nextTick();
	setupObserver();
};

const setupObserver = async () => {
	await nextTick();

	if (!sentinel.value || !scrollContainer.value) return;

	if (observer) {
		observer.disconnect();
	}

	observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];

			if (entry.isIntersecting && !storeRequests.loadingPrize && !storeRequests.allLoadedPrize) {
				// storeRequests.getWinnings("weekly");
				storeRequests.getWinnings();
			}
		},
		{
			// root: scrollContainer.value,
			rootMargin: "0px 0px 10px 0px",
			threshold: 0,
		},
	);

	observer.observe(sentinel.value);
};

let timer;
const requestTimer = async () => {
	timer = setInterval(() => {
		storeSettings.mainSettings();
	}, 60000);
};
const stopRequestTimer = () => {
	if (timer) {
		clearInterval(timer);
	}
};

const formatData = (date) => {
	if(!date) return 
	const formattedData = date.split('-').reverse();
	return `${formattedData[0]}.${formattedData[1]}.${formattedData[2]}`
}

watch(
	() => storeRequests.winnersList,
	async () => {
		await nextTick();
		setupObserver();
	},
	{ once: true, deep: true }
);

onMounted(async () => {
	await storeRequests.getWinnings();
	await requestTimer();
});
onUnmounted(() => {
	if (observer && sentinel.value) {
		observer.unobserve(sentinel.value);
		observer.disconnect();
	}
	stopRequestTimer();
});
</script>
<template>
	<section class="winners" id="winners">
		<div class="container">
			<h2 class="title winners__title">Победители</h2>
			<div class="winners__container">
				<!-- Grand Prize -->
				<div class="winners__grand-prize">
					<div class="grand-prize" v-if="grandPrize?.length">
						<h3 class="grand-prize__title title3">Главный приз</h3>
						<div class="grand-prize__image">
							<img class="full-img" width="280" height="204" src="@/assets/img/prizes/prize_grand.webp" alt="изображение" />
						</div>
						<div class="grand-prize__winner">
							<span class="grand-prize__winner-name">{{ grandPrize[0].winners[0].name }}</span>
							<span class="grand-prize__winner-email">{{ grandPrize[0].winners[0].email }}</span>
						</div>
					</div>
					<div class="grand-prize --empty" v-else>
						<h3 class="grand-prize__title title3">Главный приз</h3>
						<p class="grand-prize__desc">Заявки на участие в главном розыгрыше:</p>
						<div class="grand-prize__counter" >
							<span v-for="(digit, index) in counterDigits" :key="index">{{ digit }}</span>
						</div>
						<span class="grand-prize__line"></span>
						<p class="grand-prize__desc">Победитель будет определён 22&nbsp;декабря&nbsp;2026&nbsp;года</p>
						<button @click="openModal('modalInfo', { content: modalInfo[1], size: 'w400' })" class="grand-prize__btn-text">Как разыгрываются призы?</button>
					</div>
				</div>

				<div class="winners__block">
					<div class="winners__header">
						<div class="winners__weekly">
							<div class="winners__weekly-title">Еженедельные</div>
							<div class="winners__weekly-img">
								<img width="135" height="98" src="@/assets/img/winners/ozon.webp" alt="изображение" />
							</div>
						</div>
						<div class="winners-filter">
							<label class="winners-filter__label">
								<button class="winners-filter__search-btn" v-show="!isSearch || isEmailChanged" @click="checkEmail(), smartPixel('search_winner')">
									<svg width="24" height="24"><use href="#search"></use></svg>
								</button>
								<button class="winners-filter__search-btn --clear" v-show="isSearch && !isEmailChanged" @click="clearEmail">
									<svg width="24" height="24"><use href="#x"></use></svg>
								</button>
								<input @input="searchError = false" @keyup.enter="checkEmail" type="text" :class="['winners-filter__search', { '--error': searchError }]" :placeholder="searchError ? 'Введите Email' : 'Поиск по Email'" v-model="email" />
							</label>
						</div>
					</div>

					<div class="decor">
						<div class="decor__item"></div>
						<Transition>
							<ul :class="['winners__list', overflowAuto, customScrollBar]" v-if="storeRequests.winnersList?.length" ref="scrollContainer">
								<li class="winners__item" v-for="list in storeRequests.winnersList" :key="list.id">
									<ul class="winners__sub-list">
										<li  class="winners__sub-item --header">{{ list.title }} ({{ formatData(list.start_at) }} - {{ formatData(list.end_at) }})</li>
										<li class="winners__sub-item" v-for="item in list.winners" :key="item.id">
											<span class="winners__sub-item-name">{{ item.name }}</span>
											<span class="winners__sub-item-email">{{ item.email }}</span>
										</li>
									</ul>
								</li>
								<li v-if="isSearch && !isShowListWinners">В текущем списке победителей нет записи с указанным email. Убедитесь, что ввели адрес, который использовали при регистрации в акции</li>
								<!-- Элемент, за которым мы наблюдаем -->
								<li ref="sentinel" class="sentinel" style="position: relative; height: 30px" v-if="!storeRequests.allLoadedPrize">
									<img class="form__loader" style="position: absolute; left: 50%; transform: translateX(-50%)" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
								</li>
							</ul>
							<div class="winners__empty" v-else>
								<div class="winners__empty-inner" v-if="!storeRequests.loadingPrize">
									<p class="winners__empty-desc">Список победителей пуст.</p>
									<p class="winners__empty-desc">Для участия в розыгрыше еженедельного приза необходимо обменять листочки на билеты.</p>
									<div class="winners__empty-footer">
										<div class="winners__empty-info count-info">
											<span class="white">1 билет</span> =
											<span class="yellow">
												500 <svg width="24" height="24"><use href="#leaf"></use></svg>
											</span>
										</div>
										<BaseButtonUploadReceipt type-btn="upload_check_winners" v-if="!storeSettings.isTelegramPage" class="winners__empty-btn --small">Участвовать</BaseButtonUploadReceipt>
									</div>
								</div>
								<div class="winners__loading" v-else>
									<img class="form__loader" style="position: absolute; left: 50%; transform: translateX(-50%)" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
								</div>
							</div>
						</Transition>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
@use "@/assets/scss/base/base";

.winners {
	padding: 6.5rem 0 7.5rem;
	background-image: url("@/assets/img/winners/winners_bg.webp");
	background-repeat: no-repeat;
	background-position: center; /* x y*/
	background-size: cover; /* w h*/

	@media (max-width: vars.$x480) {
		background-image: url("@/assets/img/winners/winners_bg_mobile.webp");
		padding: 2.5rem 0 2.5rem;
	}
	&__title {
		margin-bottom: 2.5rem;
	}
	&__container {
		display: grid;
		grid-template-columns: 51.8rem 1fr;
		gap: 4.5rem;
		@media (max-width: vars.$mn) {
			grid-template-columns: 1fr;
		}
		@media (max-width: vars.$x480) {
			gap: 3.7rem;
		}
	}
	&__grand-prize {
		@media (max-width: vars.$mn) {
			max-width: 58rem;
			justify-self: center;
			width: 100%;
		}
	}
	&__block {
		margin-top: -10px;
	}

	&__header {
		display: grid;
		align-items: end;
		grid-template-columns: auto 1fr;
		gap: 10rem;
		margin-bottom: 1.8rem;
		@media (max-width: vars.$l) {
			gap: 5rem;
		}
		@media (max-width: vars.$t) {
			grid-template-columns: 1fr;
			gap: 2rem;
		}
		@media (max-width: vars.$x480) {
			gap: 0;
		}
	}

	&__weekly {
		display: flex;
		align-items: end;
		gap: 2rem;
		@media (max-width: vars.$l) {
			gap: 2rem;
		}
		@media (max-width: vars.$x480) {
			gap: 1.7rem;
			justify-content: space-between;
		}
		&-title {
			font-family: var(--second-family);
			font-weight: 700;
			font-size: 2.8rem;
			line-height: 140%;
			text-transform: uppercase;
			color: var(--text-inverse-100);
			margin-bottom: 1.3rem;
		}
		&-img {
			width: 13.5rem;
			flex-shrink: 0;
			@media (max-width: vars.$x480) {
				width: 7.9rem;
			}
		}
	}
	&__list {
		&.--overflow-auto {
			max-height: 400px;
			margin-right: -2.5rem;
			padding-right: 2.5rem;
			@media (max-width: vars.$x480) {
				margin-right: 0px;
				padding-right: 15px;
			}
		}
	}
	&__item {
		&:not(:last-child) {
			margin-bottom: 2rem;
			@media (max-width: vars.$x480) {
				margin-bottom: 1.5rem;
			}
		}
		// display: grid;
		// gap: 1rem;
		// grid-template-columns: repeat(2, 1fr);
		// @extend .base-bg;
		// border: 1px solid var(--border-accent-2-3);
		// border-radius: 2.4rem;
		// padding: 1.8rem 4.8rem;

		// font-family: var(--font-family);
		// font-weight: 500;
		// font-size: 2rem;
		// line-height: 120%;
		// color: var(--text-accent-2-100);
		// @media (max-width: vars.$x480) {
		// 	grid-template-columns: 1fr;
		// 	font-size: 1.6rem;
		// 	line-height: 140%;
		// 	border-radius: 1.6rem;
		// 	padding: 1.3rem 2rem;
		// 	gap: 0.3rem;
		// }

		// &.--header {
		// 	position: relative;
		// 	font-family: var(--second-family);
		// 	font-weight: 700;
		// 	font-size: 2rem;
		// 	line-height: 120%;
		// 	text-transform: uppercase;
		// 	color: var(--text-inverse-100);

		// 	grid-template-columns: 1fr auto 1fr;
		// 	align-items: center;
		// 	gap: 1rem;
		// 	padding: 0;
		// 	border-radius: 0;
		// 	border: none;
		// 	backdrop-filter: unset;
		// 	box-shadow: unset;
		// 	background: transparent;

		// 	@media (max-width: vars.$x480) {
		// 		grid-template-columns: 1fr;
		// 		font-weight: 400;
		// 		font-size: 1.3rem;
		// 		line-height: 160%;
		// 		justify-items: center;
		// 	}

		// 	&::before {
		// 		content: "";
		// 		width: 100%;
		// 		height: 1px;
		// 		background-color: var(--text-accent-2-100);
		// 		@media (max-width: vars.$x480) {
		// 			display: none;
		// 		}
		// 	}
		// 	&::after {
		// 		content: "";
		// 		width: 100%;
		// 		height: 1px;
		// 		background-color: var(--text-accent-2-100);
		// 		@media (max-width: vars.$x480) {
		// 			display: none;
		// 		}
		// 	}
		// }
	}

	&__sub-list {
	}

	&__sub-item {
		display: grid;
		gap: 1rem;
		grid-template-columns: repeat(2, 1fr);
		@extend .base-bg;
		border: 1px solid var(--border-accent-2-3);
		border-radius: 2.4rem;
		padding: 1.8rem 4.8rem;

		font-family: var(--font-family);
		font-weight: 500;
		font-size: 2rem;
		line-height: 120%;
		color: var(--text-accent-2-100);
		@media (max-width: vars.$x480) {
			grid-template-columns: 1fr;
			font-size: 1.6rem;
			line-height: 140%;
			border-radius: 1.6rem;
			padding: 1.3rem 2rem;
			gap: 0.3rem;
		}

		&:not(:last-child) {
			margin-bottom: 2rem;
			@media (max-width: vars.$x480) {
				margin-bottom: 15px;
			}
		}

		&.--header {
			position: relative;
			font-family: var(--second-family);
			font-weight: 700;
			font-size: 2rem;
			line-height: 120%;
			// text-transform: uppercase;
			color: var(--text-inverse-100);

			grid-template-columns: 1fr auto 1fr;
			align-items: center;
			gap: 1rem;
			padding: 0;
			border-radius: 0;
			border: none;
			backdrop-filter: unset;
			box-shadow: unset;
			background: transparent;

			@media (max-width: vars.$x480) {
				grid-template-columns: 1fr;
				font-weight: 700;
				font-size: 1.4rem;
				line-height: 160%;
				justify-items: center;
			}

			&::before {
				content: "";
				width: 100%;
				height: 1px;
				background-color: var(--text-accent-2-100);
				@media (max-width: vars.$x480) {
					display: none;
				}
			}
			&::after {
				content: "";
				width: 100%;
				height: 1px;
				background-color: var(--text-accent-2-100);
				@media (max-width: vars.$x480) {
					display: none;
				}
			}
		}
	}

	&__sub-item-name {
	}

	&__sub-item-email {
	}

	&__empty {
		position: relative;
	}
	&__empty-inner {
		display: grid;
		gap: 20px;
	}

	&__empty-desc {
		&:first-child {
			border-bottom: 1px solid var(--border-accent-2-3);
			padding-bottom: 20px;
		}
	}

	&__empty-footer {
		display: grid;
		gap: 20px;
	}

	&__empty-info {
	}

	&__empty-btn {
		width: fit-content;
		@media (max-width: vars.$x480) {
			width: 100%;
		}
	}
}

.grand-prize {
	position: relative;
	@extend .base-bg;
	border: 1px solid var(--border-accent-2-3);
	border-radius: 4rem;
	padding: 4rem 4rem;
	@media (max-width: vars.$x480) {
		border-radius: 2.4rem;
		padding: 2.5rem 2.5rem;
	}
	&.--empty {
		display: grid;
		gap: 2.4rem;
		@media (max-width: vars.$x480) {
			gap: 2rem;
		}
		&::before {
			background-image: url("@/assets/img/winners/shape.webp");
		}
		.grand-prize__title {
			margin-bottom: 0;
		}
	}
	&::before {
		position: absolute;
		content: "";
		background-image: url("@/assets/img/winners/shape_empty.webp");
		background-repeat: no-repeat;
		background-position: 0px 0px; /* x y*/
		background-size: contain; /* w h*/
		width: 16.8rem;
		height: 16.5rem;
		bottom: -23%;
		right: 3%;
		@media (max-width: vars.$x480) {
			bottom: 93%;
			right: 1%;
			width: 8rem;
			height: 7.9rem;
		}
	}
	&__title {
		color: var(--text-inverse-100);
		margin-bottom: 4rem;
		@media (max-width: vars.$x480) {
			font-size: 2.8rem;
		}
	}

	&__desc {
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
		}
	}

	&__image {
		width: 26rem;
		margin: 0 auto;
		margin-bottom: 2.3rem;
		@media (max-width: vars.$x480) {
			width: 18rem;
		}
	}

	&__counter {
		width: 100%;
		height: 7rem;
		display: grid;
		grid-template-columns: repeat(7, 1fr);

		border: 1px solid var(--border-accent-2-1);
		border-radius: 1.6rem;
		box-shadow: inset 0 0 24px 0 rgba(0, 0, 0, 0.4);
		overflow: hidden;
		span {
			display: grid;
			place-content: center;

			width: 100%;
			height: 100%;
			background: var(--fill-accent-1-1);

			font-family: var(--second-family);
			font-weight: 500;
			font-size: 3.6rem;
			line-height: 140%;
			text-align: center;
			color: var(--text-accent-2-100);

			&:not(:last-child) {
				border-right: 1px solid var(--border-accent-2-1);
			}
		}
	}

	&__line {
		width: 100%;
		height: 1px;
		background-color: var(--border-accent-2-3);
	}

	&__btn-text {
		font-weight: 500;
		font-size: 2rem;
		line-height: 120%;
		text-decoration: underline;
		text-decoration-skip-ink: none;
		color: var(--text-accent-2-100);
		text-align: start;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
		}
		@media (hover: hover) {
			&:hover {
				text-decoration: none;
			}
		}
	}

	&__winner {
		display: grid;
		gap: 0.5rem;
		font-weight: 500;
		color: var(--text-accent-2-100);
	}

	&__winner-name {
		font-size: 3.4rem;
		line-height: 140%;
		@media (max-width: vars.$x480) {
			font-size: 2.2rem;
		}
	}

	&__winner-email {
		font-size: 2rem;
		line-height: 120%;
		@media (max-width: vars.$x480) {
			font-size: 1.8rem;
		}
	}
}

.winners-filter {
	margin-bottom: 1rem;
	&__label {
		position: relative;
		width: 100%;
	}
	&__search-btn {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		right: 2.4rem;
		width: 2.4rem;
		height: 2.4rem;
		svg {
			width: 100%;
			height: 100%;
			fill: var(--text-inverse-100);
			stroke: transparent;
		}
		&.--clear {
			svg {
				fill: transparent;
				stroke: var(--text-inverse-100);
			}
		}
	}
	&__search {
		width: 100%;
		border: 1px solid var(--border-accent-2-3);
		border-radius: 1.6rem;
		padding: 1.6rem;
		font-weight: 400;
		font-size: 1.6rem;
		line-height: 160%;
		color: var(--text-inverse-100);
		&::placeholder {
			color: var(--text-inverse-100);
		}
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			padding: 1.4rem 2.2rem;
		}
		&.--error {
			border-color: var(--status-error-1);
			box-shadow: 0 0 0 4px rgba(243, 101, 101, 0.24);
		}
	}
}

.decor {
	// position: relative;
	// &__item {
	// 	position: absolute;
	// 	width: 6px;
	// 	height: 100%;
	// 	top: 0;
	// 	right: calc(-3rem + 5px);
	// 	z-index: 1;
	// 	&::before {
	// 		position: absolute;
	// 		content: "";
	// 		width: 100%;
	// 		height: 30px;
	// 		background-image: url('@/assets/img/icons/decor_list.svg');
	// 		background-repeat: no-repeat;
	// 		background-position: 0px 0px; /* x y*/
	// 		background-size: auto; /* w h*/
	// 		left: 0;
	// 		top: 0;
	// 	}
	// 	&::after {
	// 		position: absolute;
	// 		content: "";
	// 		width: 100%;
	// 		height: 30px;
	// 		background-image: url('@/assets/img/icons/decor_list.svg');
	// 		background-repeat: no-repeat;
	// 		background-position: 0px 0px; /* x y*/
	// 		background-size: auto; /* w h*/
	// 		transform: rotate(180deg);
	// 		left: 0;
	// 		bottom: 0;

	// 	}
	// }
}

.v-enter-active,
.v-leave-active {
	transition: all 0.2s ease;
}

.v-enter-from,
.v-leave-to {
	// transform: translateY(10px);
	opacity: 0;
}
</style>
